import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import { authAPI } from '../lib/api';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'no-token'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('no-token');
      return;
    }

    const verifyEmail = async () => {
      try {
        const response = await authAPI.verifyEmail(token);
        setStatus('success');
        setMessage(response.message);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login?verified=true');
        }, 3000);
      } catch (error) {
        setStatus('error');
        setMessage(error instanceof Error ? error.message : 'Verification failed');
      }
    };

    verifyEmail();
  }, [token, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-cloud py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Link to="/" className="flex justify-center mb-8">
            <img src="/logo.png" alt="ChatbotEmbed" className="h-12" />
          </Link>
          <h2 className="text-3xl font-bold text-navy-800">
            Email Verification
          </h2>
        </div>

        <div className="bg-white shadow-lg rounded-xl p-8">
          {status === 'loading' && (
            <div className="text-center">
              <Loader2 className="h-16 w-16 text-primary-500 animate-spin mx-auto" />
              <p className="mt-4 text-graphite">Verifying your email...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
              <h3 className="mt-4 text-xl font-semibold text-navy-800">Email Verified!</h3>
              <p className="mt-2 text-graphite">{message}</p>
              <p className="mt-4 text-sm text-gray-500">Redirecting to login...</p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <XCircle className="h-16 w-16 text-red-500 mx-auto" />
              <h3 className="mt-4 text-xl font-semibold text-navy-800">Verification Failed</h3>
              <p className="mt-2 text-graphite">{message}</p>
              <div className="mt-6 space-y-3">
                <Link
                  to="/login"
                  className="block w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 text-center"
                >
                  Go to Login
                </Link>
                <p className="text-sm text-gray-500">
                  Need a new verification link?{' '}
                  <Link to="/resend-verification" className="text-primary-500 hover:text-primary-600">
                    Resend verification email
                  </Link>
                </p>
              </div>
            </div>
          )}

          {status === 'no-token' && (
            <div className="text-center">
              <Mail className="h-16 w-16 text-gray-400 mx-auto" />
              <h3 className="mt-4 text-xl font-semibold text-navy-800">No Verification Token</h3>
              <p className="mt-2 text-graphite">
                Please check your email for the verification link.
              </p>
              <div className="mt-6 space-y-3">
                <Link
                  to="/login"
                  className="block w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 text-center"
                >
                  Go to Login
                </Link>
                <Link
                  to="/resend-verification"
                  className="block w-full py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 text-center"
                >
                  Resend Verification Email
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
